import { LazyLoadingSectionLogger, LoggingSection } from "@multiweb/logging";
import { ILocalizationItem } from "@multiweb/types/contracts/localizations";
import { IndexedDBPromise } from "IndexedDB/Promises/IndexedDBPromise";
import { ILocalizationStorage } from "./ILocalizationStorage";

const COMPOSITE_KEY_UNSUPPORTED = !IndexedDBPromise.isCompositeKeySupported;
const STORE_NAME = "Localizations";

const logger = LazyLoadingSectionLogger.create(LoggingSection.localizations);

/**
 * Lokální úložiště lokalizací, implementováno nad IndexedDB.
 */
export class LocalizationStorage implements ILocalizationStorage {
    private readonly _id: string;

    private __locCacheDB: Promise<IDBDatabase> | undefined;
    private get _dbName(): string {
        return `LocalizationCache|${this._id}`;
    }
    private get _locCacheDB(): Promise<IDBDatabase> {
        if (!this.__locCacheDB) {
            this.__locCacheDB = IndexedDBPromise.createOrOpenDB(this._dbName, (db) => this._createInterDB(db), 1);
        }
        return this.__locCacheDB;
    }

    constructor(id: string) {
        this._id = id;
    }

    private _createInterDB(db: IDBDatabase): void {
        const sNames = Array.prototype.slice.call(db.objectStoreNames) as string[];
        for (const sName of sNames)
            db.deleteObjectStore(sName);

        db.createObjectStore(STORE_NAME, { keyPath: !IndexedDBPromise.isCompositeKeySupported ? "_key" : ["language", "locKey"] as [keyof ILocalizationItem, keyof ILocalizationItem] });
    }

    public async saveLocalizations(language: string, data: readonly ILocalizationItem[]): Promise<void> {
        // neni zde mozne volat Localization.GetLocalizationsDBStore(true) jelikoz transakci pro zapis nemuzu vratit v promise, protoze pri spusteni then uz nemusi byt platna
        try {
            const db = await this._locCacheDB;
            const store = db.transaction(STORE_NAME, "readwrite").objectStore(STORE_NAME);
            let excLogged = false;
            for (const li of data) {
                const lidb: ILocalizationItem = { language, locKey: li.locKey, primaryText: li.primaryText, secondaryText: li.secondaryText };
                if (COMPOSITE_KEY_UNSUPPORTED)
                    (lidb as any)["_key"] = lidb.language + "|" + lidb.locKey; //JSON.stringify([lidb.Lang, lidb.Code]);
                try {
                    store.put(lidb);
                } catch (e) {
                    if (!excLogged) {
                        logger.error(e);
                        excLogged = true;
                    }
                }
            }
        } catch (ex) {
            logger.error(ex);
        }
    }

    public async delete(): Promise<void> {
        const db = await this._locCacheDB;
        await IndexedDBPromise.clearStore(db, STORE_NAME);
    }

    public async getLocalizations(language: string, locKeys: readonly string[]): Promise<readonly ILocalizationItem[]> {
        const values: any[] = !COMPOSITE_KEY_UNSUPPORTED ? locKeys.map((v) => [language, v]) : locKeys.map((v) => language + "|" + v);

        const db = await this._locCacheDB;
        //return AsyncIteratorUtils.ToArray<LocalizationItem>(IndexedDB.IndexedDBPromise.getRowsByIndexValues1(db, STORE_NAME, values));
        return await IndexedDBPromise.getRowsByIndexValues(db, STORE_NAME, values);
    }
}
